import { useSearchParams } from 'next/navigation';
import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import { TriggeredByType } from '@/libraries/contexts/search-context';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryAvailableOffersBestOffersData = TravelPortal.Responses.BestOffers;

interface UseQueryAvailableOffersBestOffersOptions {
  initialData?: UseQueryAvailableOffersBestOffersData;
  triggeredBy?: TriggeredByType;
}

const useQueryAvailableOffersBestOffers = (
  options?: UseQueryAvailableOffersBestOffersOptions,
) => {
  const locale = getLocale();
  const searchParams = useSearchParams();

  const query = useQuery<UseQueryAvailableOffersBestOffersData>(
    [QueryId.AVAILABLE_OFFERS_BEST_OFFERS, locale, searchParams!.toString()],
    async () => {
      const baseUrl = '/api/available-offers/best-offers';

      const searchParameters = new URLSearchParams({
        adultsNumber: String(searchParams!.get('adultsNumber')),
        area: String(searchParams!.get('area')),
        durationType: String(searchParams!.get('durationType')),
      });

      if (searchParams!.get('childrenAges')) {
        searchParameters.append(
          'childrenAges',
          String(searchParams!.get('childrenAges')),
        );
      }

      if (searchParams!.get('departureIds')) {
        searchParameters.append(
          'departureIds',
          String(searchParams!.get('departureIds')),
        );
      }

      if (searchParams!.get('countryIds')) {
        searchParameters.append(
          'countryIds',
          String(searchParams!.get('countryIds')),
        );
      }

      if (searchParams!.get('destinationIds')) {
        searchParameters.append(
          'destinationIds',
          String(searchParams!.get('destinationIds')),
        );
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryAvailableOffersBestOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!searchParams!.get('adultsNumber') &&
        !!searchParams!.get('area') &&
        !!searchParams!.get('durationType') &&
        options?.triggeredBy !== 'calendarSlider',
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryAvailableOffersBestOffers;
